import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { User } from '@vineyard/types'
import {
  getAccessToken,
  refreshAccessToken,
  clearTokens,
  getApiUrl,
} from './auth'
import { apiFetch } from './api'

interface AuthContextValue {
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  signOut: async () => {},
  refreshUser: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const loadUser = useCallback(async () => {
    try {
      let token = getAccessToken()

      // 메모리에 토큰 없으면 refresh 시도 (refresh_token은 httpOnly cookie로 자동 전송)
      if (!token) {
        token = await refreshAccessToken()
      }

      if (!token) {
        setLoading(false)
        return
      }

      const profile = await apiFetch<User>('/api/auth/profile')
      setUser(profile)
    } catch {
      clearTokens()
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  const signOut = useCallback(async () => {
    try {
      const token = getAccessToken()
      // refresh_token은 httpOnly cookie로 Workers API가 자동 읽고 삭제함 — body 불필요
      await fetch(`${getApiUrl()}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
    } catch {
      // Logout failure is non-critical
    } finally {
      clearTokens()
      setUser(null)
      window.location.href = '/login'
    }
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const profile = await apiFetch<User>('/api/auth/profile')
      setUser(profile)
    } catch {
      // ignore
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
